import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const LegalPage = () => {
  const { doc } = useParams();
  const { t } = useLanguage();

  if (!["privacy", "terms", "impressum"].includes(doc)) {
    return <Navigate to="/" replace />;
  }

  const title = t(`legal.${doc}Title`);
  const sections = doc === "privacy" ? t("legal.privacySections") : doc === "terms" ? t("legal.termsSections") : null;

  return (
    <div data-testid={`legal-page-${doc}`} className="min-h-screen bg-[#08080A]">
      <header className="sticky top-0 z-30 border-b border-gold/10 bg-[#08080A]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="legal-logo" className="flex items-center">
            <span className="font-display text-2xl font-bold tracking-[0.1em] text-white">
              A<span className="text-gold-gradient">&amp;</span>A <span className="text-gold">MOTORS</span>
            </span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold hover:text-gold-light">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
          {t("legal.backHome")}
        </Link>
        <h1 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">{title}</h1>

        {doc === "impressum" ? (
          <div className="mt-10 space-y-6">
            <p className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-5 text-sm leading-relaxed text-gold-light" data-testid="impressum-note">
              {t("legal.impressumNote")}
            </p>
            <ul className="space-y-3 rounded-2xl border border-white/[0.07] bg-panel p-7">
              {t("legal.impressumFields").map((field, i) => {
                const placeholder = field.includes("[");
                return (
                  <li key={i} data-testid={`impressum-field-${i}`} className={`text-sm ${placeholder ? "text-gold/90" : "text-slate-200"}`}>
                    {field}
                  </li>
                );
              })}
            </ul>
            <div className="rounded-2xl border border-white/[0.07] bg-panel p-7 text-sm text-slate-300">
              <p className="font-semibold text-white">{t("footer.contactTitle")}</p>
              <p className="mt-2" dir="ltr">
                UAE: {CONTACT.uaeDisplay} · Germany: {CONTACT.deDisplay}
              </p>
              <p className="mt-2">Dubai, United Arab Emirates</p>
            </div>
          </div>
        ) : (
          <div className="mt-10 space-y-6">
            {sections.map((section, i) => (
              <section key={i} data-testid={`legal-section-${i}`} className="rounded-2xl border border-white/[0.07] bg-panel p-7">
                <h2 className="font-display text-xl font-semibold text-gold">{section.h}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">{section.p}</p>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LegalPage;
